import type { MedicationRequest } from '@/services/med-req-detail'
import type { Order } from '@/types/order'

export interface TemplateVariable {
  key: string
  label: string
  group: string
  resolve: (medRequest: MedicationRequest, order?: Order | null) => string
}

export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  {
    key: 'patient_first_name',
    label: 'First Name',
    group: 'Patient',
    resolve: mr => mr.patient?.firstName || ''
  },
  {
    key: 'patient_last_name',
    label: 'Last Name',
    group: 'Patient',
    resolve: mr => mr.patient?.lastName || ''
  },
  {
    key: 'patient_dob',
    label: 'Date of Birth',
    group: 'Patient',
    resolve: mr => mr.patient?.dob || ''
  },
  {
    key: 'prescribed_item',
    label: 'Prescribed Item',
    group: 'Medication',
    resolve: mr => mr.orderedItem.name || ''
  },
  {
    key: 'dosage',
    label: 'Dosage',
    group: 'Medication',
    resolve: mr => mr.orderedItem.dose || ''
  },
  {
    key: 'quantity',
    label: 'Quantity',
    group: 'Medication',
    resolve: mr => mr.orderedItem.quantity || ''
  },
  {
    key: 'product_category',
    label: 'Product Category',
    group: 'Medication',
    resolve: mr => mr.orderedItem.category || ''
  },
  {
    key: 'prescriber',
    label: 'Prescriber',
    group: 'Order',
    resolve: mr => mr.prescriber?.fullName || ''
  },
  {
    key: 'approver',
    label: 'Approver',
    group: 'Order',
    resolve: mr => mr.approvedBy || mr.prescriber?.fullName || ''
  },
  {
    key: 'order_number',
    label: 'Order Number',
    group: 'Order',
    resolve: mr => mr.orderNumber || ''
  },
  {
    key: 'approved_date',
    label: 'Approved Date',
    group: 'Order',
    resolve: mr => mr.prescriptionIssueDate || mr.closingDate || mr.lastUpdateOn || ''
  },
  {
    key: 'shipping_address_line1',
    label: 'Shipping Address Line 1',
    group: 'Shipping',
    resolve: (_mr, order) => order?.shipping?.address1 || ''
  },
  {
    key: 'shipping_address_line2',
    label: 'Shipping Address Line 2',
    group: 'Shipping',
    resolve: (_mr, order) => order?.shipping?.address2 || ''
  },
  {
    key: 'shipping_postcode',
    label: 'Shipping Postcode',
    group: 'Shipping',
    resolve: (mr, order) => order?.shipping?.postCode || mr.orderPostCode || ''
  },
  {
    key: 'gp_name',
    label: 'GP Name',
    group: 'GP',
    resolve: mr => mr.gp?.name || ''
  },
  {
    key: 'gp_address_line1',
    label: 'GP Address Line 1',
    group: 'GP',
    resolve: mr => mr.gp?.address_line_1 || ''
  },
  {
    key: 'gp_address_line2',
    label: 'GP Address Line 2',
    group: 'GP',
    resolve: mr => mr.gp?.address_line_2 || ''
  },
  {
    key: 'gp_address_line3',
    label: 'GP Address Line 3',
    group: 'GP',
    resolve: mr => mr.gp?.address_line_3 || ''
  },
  {
    key: 'gp_postcode',
    label: 'GP Postcode',
    group: 'GP',
    resolve: mr => mr.gp?.postcode || ''
  },
  {
    key: 'gp_county',
    label: 'GP County',
    group: 'GP',
    resolve: mr => mr.gp?.county || ''
  }
]

// Shared with RichTextEditor.tsx so the editor's variable-node parsing and this
// interpolation step can never silently drift on the token format.
export const VARIABLE_TOKEN_PATTERN = /\{\{(\w+)\}\}/g

export function interpolateTemplate(content: string, medRequest: MedicationRequest, order?: Order | null): string {
  // If a variable resolves to an empty value (e.g. a null DOB), leave the
  // token in place so findUnresolvedVariables() can flag it instead of
  // silently vanishing.
  return content.replace(VARIABLE_TOKEN_PATTERN, (match, key) => {
    const variable = TEMPLATE_VARIABLES.find(v => v.key === key)
    if (!variable) return match
    const value = variable.resolve(medRequest, order)
    return value !== '' ? value : match
  })
}

/** Finds any `{{key}}` placeholders left unresolved after interpolateTemplate(). */
export function findUnresolvedVariables(content: string): string[] {
  const matches = content.match(VARIABLE_TOKEN_PATTERN)
  return matches ? [...new Set(matches)] : []
}

/** Formats an unresolved placeholder token for display — its friendly label, falling back to the raw key. */
export function formatUnresolvedVariable(token: string): string {
  const key = token.slice(2, -2)
  return TEMPLATE_VARIABLES.find(v => v.key === key)?.label ?? key
}
